import React from 'react';
import Icon from '../../../components/AppIcon';

const OrderSummary = ({ orderData, discounts = [] }) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const calculateSubtotal = () => {
    return orderData?.items?.reduce((sum, item) => sum + item?.amount, 0);
  };

  const calculateDiscountAmount = () => {
    return discounts?.reduce((sum, discount) => sum + discount?.amount, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscountAmount();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Receipt" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Order Summary</h2>
      </div>
      {/* Order Items */}
      <div className="space-y-4 mb-6">
        {orderData?.items?.map((item, index) => (
          <div key={index} className="flex items-start justify-between py-3 border-b border-border last:border-b-0">
            <div className="flex-1">
              <h3 className="font-medium text-foreground">{item?.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{item?.description}</p>
              {item?.duration && (
                <div className="flex items-center space-x-1 mt-2">
                  <Icon name="Clock" size={14} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{item?.duration}</span>
                </div>
              )}
            </div>
            <div className="text-right ml-4">
              <div className="font-mono font-medium text-foreground">
                {formatAmount(item?.amount)}
              </div>
              {item?.originalAmount && item?.originalAmount > item?.amount && (
                <div className="text-xs text-muted-foreground line-through">
                  {formatAmount(item?.originalAmount)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Discounts */}
      {discounts?.length > 0 && (
        <div className="space-y-2 mb-6">
          <h4 className="text-sm font-medium text-foreground">Applied Discounts</h4>
          {discounts?.map((discount, index) => (
            <div key={index} className="flex items-center justify-between py-2 px-3 bg-success/10 border border-success/20 rounded-md">
              <div className="flex items-center space-x-2">
                <Icon name="Tag" size={16} className="text-success" />
                <span className="text-sm text-success font-medium">{discount?.code}</span>
                <span className="text-xs text-muted-foreground">({discount?.description})</span>
              </div>
              <span className="text-sm font-medium text-success">
                -{formatAmount(discount?.amount)}
              </span>
            </div>
          ))}
        </div>
      )}
      {/* Totals */}
      <div className="space-y-2 pt-4 border-t border-border">
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-mono text-foreground">{formatAmount(calculateSubtotal())}</span>
        </div>
        
        {discounts?.length > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Total Discounts</span>
            <span className="font-mono text-success">-{formatAmount(calculateDiscountAmount())}</span>
          </div>
        )}
        
        <div className="flex justify-between items-center pt-2 border-t border-border">
          <span className="text-lg font-semibold text-foreground">Total</span>
          <span className="text-xl font-bold font-mono text-primary">
            {formatAmount(calculateTotal())}
          </span>
        </div>
      </div>
      {/* Order Info */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-foreground mb-1">Order Information</p>
            <p className="text-muted-foreground">
              Order ID: #{orderData?.orderId} • Created: {new Date()?.toLocaleDateString('fr-CM')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;